// Saving Global Settings: builds the attribute payload and asks the user to
// confirm before dropping location attributes that were removed.

import { useCallback, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import { t } from '@lingui/macro';

import { stripEditedFlag } from '../../../features/dynamic-form';
import IntegrationsApi from '../../../api/integrations';

const LOCATION_SETTINGS_TAG = 'location_settings_input';

// When an access_token is present and the password field was left blank, the
// username and password are dropped from the payload: sending a filled username
// with an empty password makes the backend retry the login and fail.
const formatUserPasswordAccessTokenFields = (attributes) => {
  const hasAccessToken = attributes.some((item) =>
    /^access_token\d*$/.test(item.name),
  );

  if (!hasAccessToken) return attributes;

  const hasEmptyPassword = attributes.some(
    (item) => /^password\d*$/.test(item.name) && isEmpty(item.value),
  );

  if (!hasEmptyPassword) return attributes;

  return attributes.filter(
    (item) =>
      !/^username\d*$/.test(item.name) && !/^password\d*$/.test(item.name),
  );
};

const attributeNamesIn = (definitions) =>
  (Array.isArray(definitions) ? definitions : [])
    .map((definition) => definition.attribute_name)
    .filter(Boolean);

export default function useIntegrationSubmit({
  integration,
  form,
  metadataEditor,
  conditions,
  panels,
  setDisableSubmit,
  onSaved,
  notifyResult,
}) {
  const [saving, setSaving] = useState(false);
  const [removedNames, setRemovedNames] = useState([]);
  const [confirmVisible, setConfirmVisible] = useState(false);

  const attrs = integration.attributes;

  // Diffs the attribute names authored by every location_settings_input field
  // against what was last saved, so removals can be confirmed before they land.
  const detectRemovedLocationAttributes = useCallback(() => {
    const authoring = (attrs.global_settings_attributes || []).filter(
      (a) => a.html_tag === LOCATION_SETTINGS_TAG,
    );

    const original = authoring.flatMap((a) => attributeNamesIn(a.value));
    const current = authoring.flatMap((a) =>
      attributeNamesIn(form.values[a.name]),
    );

    return original.filter((name) => !current.includes(name));
  }, [attrs.global_settings_attributes, form.values]);

  const doSubmit = useCallback(async () => {
    setDisableSubmit(true);
    setSaving(true);

    const attributes = stripEditedFlag(
      formatUserPasswordAccessTokenFields(form.buildAttributes()),
    );
    const panelValues = panels.submitValues;

    let data;
    try {
      data = await IntegrationsApi.updateIntegrationStaticAttributes(
        integration.id,
        attributes,
        metadataEditor.toObject(),
        panelValues.cacheEnabled,
        panelValues.cacheTTL,
        panelValues.circuitBreakerEnabled,
        panelValues.circuitBreakerFailureThreshold,
        panelValues.circuitBreakerRetryAfter,
        panelValues.automaticRetryEnabled,
        panelValues.retryAfter,
        panelValues.maxRetries,
        conditions.toPayload(),
        panelValues.timeout,
        panelValues.autoEnable,
      );
    } catch (e) {
      data = false;
    }

    if (data?.data?.id) {
      onSaved(data.data);
      notifyResult(
        'success',
        t`Updated Integration Settings`,
        t`${attrs.name} was successfully updated.`,
      );
    } else {
      const detail = (data?.errors || [])
        .map((error) => error.detail)
        .filter(Boolean)
        .join(' ');

      notifyResult(
        'warning',
        t`Unable to Update Integration Settings`,
        detail
          ? t`Failed to update ${attrs.name}: ${detail}`
          : t`Failed to update ${attrs.name}.`,
      );
      setDisableSubmit(false);
    }

    setSaving(false);
  }, [
    integration,
    attrs.name,
    form,
    metadataEditor,
    conditions,
    panels,
    setDisableSubmit,
    onSaved,
    notifyResult,
  ]);

  const submit = useCallback(async () => {
    const removed = detectRemovedLocationAttributes();

    if (removed.length > 0) {
      setRemovedNames(removed);
      setConfirmVisible(true);
      return;
    }

    await doSubmit();
  }, [detectRemovedLocationAttributes, doSubmit]);

  const confirmRemoval = useCallback(() => {
    setConfirmVisible(false);
    doSubmit();
  }, [doSubmit]);

  return {
    saving,
    submit,
    removedNames,
    confirmVisible,
    confirmRemoval,
    cancelRemoval: () => setConfirmVisible(false),
  };
}
